import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  List,
  ListItem,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { getGetJournalByPatientQueryKey } from '../../../api/generated/journal-controller/journal-controller.ts';
import {
  useFillAndStoreLetter,
  useGetLetterPlaceholders,
} from '../../../api/generated/letter-controller/letter-controller.ts';
import { useGetPatientById } from '../../../api/generated/patient-controller/patient-controller.ts';
import { FileInput } from '../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { getLetterPlaceholderName } from '../../../utils/letter.ts';

interface FormValues {
  file: File | null;
}

export function CreateLetterDialog({
  patientId,
  open,
  onClose,
}: {
  patientId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: patient } = useGetPatientById(patientId);
  const { data: placeholders } = useGetLetterPlaceholders();

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      file: null,
    },
  });

  const createLetterMutation = useFillAndStoreLetter({
    mutation: {
      onSuccess: () => {
        toast.success('Notiz wurde erfolgreich gespeichert');
        void queryClient.invalidateQueries({
          queryKey: getGetJournalByPatientQueryKey({ patientId }),
        });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Ausfüllen des Berichts ist ein Fehler aufgetreten');
      },
    },
  });

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700 }}>
        <ModalClose />
        <Typography level={'h4'}>
          Bericht ausfüllen ({patient?.lastName} {patient?.firstName})
        </Typography>
        <Divider />
        <Stack>
          <Typography level={'body-sm'}>
            Laden Sie eine Word-Datei hoch, in welche die Daten des Patienten eingefüllt werden sollen. Mögliche
            Platzhalter:
          </Typography>
          <List
            marker={'disc'}
            sx={{
              '--List-gap': '-15px',
            }}>
            {placeholders?.map((placeholder) => (
              <ListItem key={placeholder}>
                <Stack direction={'row'}>
                  <Typography level={'body-xs'} fontWeight={'bold'}>
                    ${placeholder}$
                  </Typography>
                  <Typography level={'body-xs'}>: {getLetterPlaceholderName(placeholder)}</Typography>
                </Stack>
              </ListItem>
            ))}
          </List>
        </Stack>
        <form
          onSubmit={handleSubmit((data) => {
            createLetterMutation.mutate({
              data: { file: data.file! },
              params: {
                patientId: patientId,
              },
            });
          })}>
          <Stack gap={2}>
            <FormControl sx={{ flex: 1 }} error={!!errors.file}>
              <FormLabel>
                <Typography>Bericht *</Typography>
              </FormLabel>
              <Controller
                name={'file'}
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <FileInput
                    value={value}
                    onChange={onChange}
                    accept={'application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
                    placeholder={'Datei hochladen'}
                  />
                )}
              />
              {errors.file && <FormHelperText>Bericht muss hochgeladen werden</FormHelperText>}
            </FormControl>

            <FormActionButtons onAbortClick={handleOnClose} disableSubmit={!isFormDirty} />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
